import API from '../config/api';
import React, { useState, useEffect } from 'react';

import axios from 'axios';

import { Container, Row, Col, Card, Button } from 'react-bootstrap';
import { FaEye } from 'react-icons/fa';

import { Link, useLocation } from 'react-router-dom';

import QuickViewModal from './QuickViewModal';
import StarRating from './product/StarRating';
import { useProductRatings } from '../hooks/useProductRatings';

function ProductSection() {
  const [products, setProducts] = useState([]);

  const [loading, setLoading] = useState(true);

  const [quickViewProduct, setQuickViewProduct] = useState(null);

  const location = useLocation();
  const isAdmin = JSON.parse(
    localStorage.getItem('userInfo') || 'null'
  )?.isAdmin;

  const { getRating } = useProductRatings();

  useEffect(() => {
    const fetchTopProducts = async () => {
      try {
        const { data } = await axios.get(
          `${API}/products/topsale`
        );

        setProducts(data);
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    };

    fetchTopProducts();
  }, []);

  return (
    <div className="product-section">
      <Container>
        {/* Section heading lives in its own showcase card */}
        <Card className="border-0 mb-5 product-hero">
          <span className="product-hero-glow product-hero-glow-1" />
          <span className="product-hero-glow product-hero-glow-2" />
          <span className="product-hero-rays" />
          <span className="product-hero-shine" />

          <Card.Body className="position-relative text-center p-4 p-md-5">
            <span className="product-hero-eyebrow">
              <span className="product-hero-spark">✦</span> Trending Now
              <span className="product-hero-spark">✦</span>
            </span>

            <h2 className="fw-bold product-heading">
              <span className="product-heading-flame">🔥</span> Top Sale Products
            </h2>

            <span className="product-heading-accent" />

            <p className="mx-auto product-subtitle">
              Discover our most popular products loved by customers and trending
              right now.
            </p>

            <div className="product-hero-chips">
              <span className="product-hero-chip">
                ⭐ Customer favourites
              </span>
              <span className="product-hero-chip">🚚 Free delivery</span>
              <span className="product-hero-chip">🛡 Easy refunds</span>
              {!loading && products.length > 0 && (
                <span className="product-hero-chip is-count">
                  {products.length} picks this week
                </span>
              )}
            </div>

            <Button
              as={Link}
              to="/products"
              className="product-hero-cta fw-semibold mt-4"
            >
              Explore All Products{' '}
              <span className="product-btn-arrow">→</span>
            </Button>
          </Card.Body>
        </Card>

        {loading ? (
          <Row className="g-4">
            {[1, 2, 3].map((n) => (
              <Col lg={4} md={6} sm={12} key={n}>
                <Card
                  className="border-0 shadow-sm h-100 product-skeleton"
                  style={{ borderRadius: '24px' }}
                >
                  <div className="skeleton-box" style={{ height: '280px' }} />
                  <Card.Body>
                    <div className="skeleton-line w-75 mb-3" />
                    <div
                      className="skeleton-line w-50 mb-3"
                      style={{ height: '10px' }}
                    />
                    <div className="skeleton-line w-100 mb-2" />
                    <div className="skeleton-line w-100 mb-4" />
                    <div
                      className="skeleton-line w-25 mb-4"
                      style={{ height: '22px' }}
                    />
                    <div
                      className="skeleton-line w-100"
                      style={{ height: '42px', borderRadius: '12px' }}
                    />
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        ) : products.length === 0 ? (
          <Card
            className="border-0 shadow-sm text-center p-5 mx-auto"
            style={{ borderRadius: '24px', maxWidth: '520px' }}
          >
            <div style={{ fontSize: '3rem' }}>🛒</div>
            <h4 className="fw-bold mt-2 mb-1">No Top Sale Products Yet</h4>
            <p className="text-muted mb-0">
              Check back soon for trending picks.
            </p>
          </Card>
        ) : (
          <Row className="g-4">
            {products.map((product, index) => {
              const inStock = product.stock > 0;
              const lowStock = inStock && product.stock <= 5;

              return (
                <Col xl={4} lg={4} md={6} sm={12} key={product._id}>
                  <Card
                    className="border-0 shadow-lg h-100 product-card product-fade-in"
                    style={{ animationDelay: `${index * 0.08}s` }}
                  >
                    {/* Embers drift up the card on hover, like sparks off a diya */}
                    <span className="product-embers" aria-hidden="true" />

                    <div className="product-img-wrap">
                      <Card.Img
                        variant="top"
                        src={product.image}
                        alt={product.name}
                        className="product-image"
                      />

                      {/* Blessing aura + sparks, offset per card so the row
                          never pulses in unison */}
                      <span
                        className="product-aura"
                        aria-hidden="true"
                        style={{ animationDelay: `${index * 0.55}s` }}
                      />
                      <span
                        className="product-spark product-spark-1"
                        aria-hidden="true"
                        style={{ animationDelay: `${index * 0.4}s` }}
                      >
                        ✦
                      </span>
                      <span
                        className="product-spark product-spark-2"
                        aria-hidden="true"
                        style={{ animationDelay: `${index * 0.4 - 1.6}s` }}
                      >
                        ✦
                      </span>

                      <span className="product-badge">
                        <span className="product-badge-flame">🪔</span> Top Sale
                      </span>

                      {!inStock ? (
                        <span className="product-stock-badge is-out">
                          Sold Out
                        </span>
                      ) : lowStock ? (
                        <span className="product-stock-badge is-low">
                          🔥 Only {product.stock} left
                        </span>
                      ) : null}

                      <div className="product-overlay">
                        <button
                          type="button"
                          className="product-overlay-btn"
                          onClick={() => setQuickViewProduct(product)}
                        >
                          <FaEye className="me-1" /> Quick View
                        </button>
                      </div>
                    </div>

                    <Card.Body className="d-flex flex-column">
                      <Card.Title className="fw-bold mb-1 product-name">
                        {product.name}
                      </Card.Title>

                      <div className="mb-2 product-stars">
                        <StarRating
                          average={getRating(product._id).averageRating}
                          count={getRating(product._id).totalReviews}
                        />
                      </div>

                      <Card.Text
                        className="text-muted mb-3"
                        style={{ minHeight: '48px' }}
                      >
                        {product.shortDescription}
                      </Card.Text>

                      <div className="product-price-plate mb-3">
                        <span className="product-price">
                          <span className="product-price-cur">₹</span>
                          {Number(product.price).toLocaleString('en-IN')}
                        </span>

                        <span className="product-ship">
                          <span className="product-ship-ico">🚚</span>
                          Free delivery
                        </span>
                      </div>

                      <Button
                        as={Link}
                        to={`/product/${product._id}`}
                        variant="dark"
                        className="mt-auto w-100 product-btn"
                      >
                        View Details{' '}
                        <span className="product-btn-arrow">→</span>
                      </Button>

                      {isAdmin && (
                        <Button
                          as={Link}
                          to={`/admin/edit-product/${product._id}`}
                          state={{ from: location.pathname + location.search }}
                          variant="warning"
                          className="w-100 fw-semibold mt-2 product-btn"
                        >
                          ✏️ Edit Product
                        </Button>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              );
            })}
          </Row>
        )}
      </Container>

      <QuickViewModal
        product={quickViewProduct}
        show={!!quickViewProduct}
        onHide={() => setQuickViewProduct(null)}
      />

      <style>{productStyles}</style>
    </div>
  );
}

const productStyles = `
  .product-section {
    /* Divine backdrop: soft violet + saffron glows on a warm gold base */
    background:
      radial-gradient(760px 320px at 12% -10%, rgba(124,58,237,0.14), transparent 70%),
      radial-gradient(760px 320px at 88% 110%, rgba(234,88,12,0.16), transparent 70%),
      linear-gradient(135deg, #faf5ff 0%, #fff7ed 45%, #fef3c7 100%);
    padding: clamp(40px, 8vw, 70px) 0;
  }

  /* ===== Section heading showcase card ===== */
  .product-hero {
    position: relative;
    overflow: hidden;
    border-radius: 28px;
    color: #fff7ed;
    background:
      radial-gradient(120% 140% at 50% -20%, rgba(253,230,138,0.22), transparent 60%),
      linear-gradient(135deg, #1c0a03 0%, #7c2d12 48%, #c2410c 100%);
    box-shadow: 0 26px 60px rgba(124, 45, 18, 0.4) !important;
  }
  /* Gold filigree border, drawn inside so the corners stay crisp */
  .product-hero::after {
    content: '';
    position: absolute;
    inset: 10px;
    border: 1px solid rgba(253, 230, 138, 0.35);
    border-radius: 20px;
    pointer-events: none;
  }
  .product-hero-glow {
    position: absolute;
    border-radius: 50%;
    filter: blur(70px);
    pointer-events: none;
  }
  .product-hero-glow-1 {
    width: 260px; height: 260px;
    background: #f59e0b;
    opacity: 0.5;
    top: -120px; left: 8%;
    animation: heroFloat 10s ease-in-out infinite;
  }
  .product-hero-glow-2 {
    width: 220px; height: 220px;
    background: #7c3aed;
    opacity: 0.35;
    bottom: -120px; right: 6%;
    animation: heroFloat 13s ease-in-out infinite reverse;
  }
  /* Faint sunburst rays behind the title */
  .product-hero-rays {
    position: absolute;
    top: 50%; left: 50%;
    width: 760px; height: 760px;
    transform: translate(-50%, -50%);
    background: repeating-conic-gradient(from 0deg, rgba(253,230,138,0.10) 0deg 6deg, transparent 6deg 18deg);
    mask-image: radial-gradient(circle, #000 0%, transparent 62%);
    -webkit-mask-image: radial-gradient(circle, #000 0%, transparent 62%);
    pointer-events: none;
    animation: heroSpin 60s linear infinite;
  }
  /* Slow gold sweep across the card */
  .product-hero-shine {
    position: absolute;
    top: 0; left: -50%;
    width: 40%; height: 100%;
    background: linear-gradient(100deg, transparent, rgba(253,230,138,0.16), transparent);
    transform: skewX(-18deg);
    pointer-events: none;
    animation: heroSweep 7s ease-in-out infinite;
  }

  .product-hero-eyebrow {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 0.7rem;
    font-weight: 800;
    letter-spacing: 3px;
    text-transform: uppercase;
    color: #fde68a;
    background: rgba(253, 230, 138, 0.1);
    border: 1px solid rgba(253, 230, 138, 0.4);
    border-radius: 30px;
    padding: 6px 18px;
    margin-bottom: 16px;
  }
  .product-hero-spark {
    display: inline-block;
    animation: heroTwinkle 2.4s ease-in-out infinite;
  }
  .product-hero-spark:last-child { animation-delay: 1.2s; }

  .product-heading {
    font-size: clamp(1.9rem, 5vw, 3rem);
    letter-spacing: -0.5px;
    background: linear-gradient(120deg, #fff7ed 10%, #fde68a 45%, #fbbf24 70%, #fff7ed 95%);
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
    text-shadow: 0 6px 24px rgba(0,0,0,0.25);
  }
  .product-heading-flame {
    -webkit-text-fill-color: initial;
    color: initial;
    display: inline-block;
    animation: heroFlicker 2.6s ease-in-out infinite;
  }
  .product-heading-accent {
    display: block;
    width: 120px;
    height: 3px;
    margin: 16px auto 18px;
    border-radius: 4px;
    background: linear-gradient(90deg, transparent, #fbbf24, #fde68a, #fbbf24, transparent);
  }
  .product-subtitle {
    max-width: 620px;
    font-size: clamp(0.95rem, 2.5vw, 1.05rem);
    color: rgba(255, 247, 237, 0.78);
  }

  .product-hero-chips {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 10px;
    margin-top: 22px;
  }
  .product-hero-chip {
    font-size: 0.78rem;
    font-weight: 600;
    color: #fff7ed;
    background: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(253, 230, 138, 0.28);
    border-radius: 30px;
    padding: 7px 16px;
    backdrop-filter: blur(6px);
    transition: transform 0.2s ease, background 0.2s ease;
  }
  .product-hero-chip:hover {
    transform: translateY(-2px);
    background: rgba(255, 255, 255, 0.18);
  }
  .product-hero-chip.is-count {
    color: #7c2d12;
    background: linear-gradient(135deg, #fde68a, #fbbf24);
    border-color: transparent;
    font-weight: 800;
  }

  .product-hero-cta {
    border: none;
    border-radius: 30px;
    padding: 12px 30px;
    color: #4a1608;
    background: linear-gradient(135deg, #fde68a, #fbbf24 55%, #f59e0b);
    box-shadow: 0 12px 26px rgba(251, 191, 36, 0.32);
    transition: transform 0.25s ease, box-shadow 0.25s ease, filter 0.25s ease;
  }
  .product-hero-cta:hover,
  .product-hero-cta:focus {
    color: #4a1608;
    filter: brightness(1.06);
    transform: translateY(-3px);
    box-shadow: 0 18px 34px rgba(251, 191, 36, 0.45);
  }

  @keyframes heroFloat {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-16px); }
  }
  @keyframes heroSpin {
    from { transform: translate(-50%, -50%) rotate(0deg); }
    to   { transform: translate(-50%, -50%) rotate(360deg); }
  }
  @keyframes heroSweep {
    0%, 65% { left: -50%; }
    100% { left: 130%; }
  }
  @keyframes heroTwinkle {
    0%, 100% { opacity: 0.35; transform: scale(0.85); }
    50% { opacity: 1; transform: scale(1.15); }
  }
  @keyframes heroFlicker {
    0%, 100% { transform: rotate(-4deg) scale(1); }
    50% { transform: rotate(4deg) scale(1.12); }
  }

  /*
    The border is a gradient, not a colour, so it needs two painted layers:
    the cream fill is clipped to the padding box and the gold ribbon to the
    border box, which leaves the ribbon showing only in the 2px border ring.
    Its background-size is oversized on that layer alone so the light can
    travel along it without dragging the fill with it.
  */
  .product-card {
    position: relative;
    border-radius: 24px;
    overflow: hidden;
    border: 2px solid transparent !important;
    background:
      linear-gradient(165deg, #fffdf9 0%, #fff5e6 55%, #fdeccb 100%) padding-box,
      linear-gradient(115deg, #fbbf24, #f59e0b 22%, #fde68a 42%, #ea580c 62%, #fbbf24 88%) border-box !important;
    background-size: 100% 100%, 260% 100% !important;
    transition: transform 0.4s cubic-bezier(0.22, 1, 0.36, 1),
                box-shadow 0.4s ease;
    animation: divineRim 9s linear infinite;
  }
  @keyframes divineRim {
    from { background-position: 0 0, 0% 50%; }
    to   { background-position: 0 0, 260% 50%; }
  }

  /*
    Embers rising on hover. Each pseudo-element is one particle whose
    box-shadow paints five more at fixed offsets, so twelve embers cost two
    elements and one animated transform rather than twelve of each.
  */
  .product-embers {
    position: absolute;
    inset: 0;
    z-index: 5;
    pointer-events: none;
    border-radius: 22px;
    overflow: hidden;
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  .product-card:hover .product-embers { opacity: 1; }

  .product-embers::before,
  .product-embers::after {
    content: '';
    position: absolute;
    bottom: -14px;
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: #fbbf24;
    box-shadow:
       34px 0 0 -1px rgba(253, 230, 138, 0.95),
       78px 0 0  0   rgba(251, 191, 36, 0.75),
      126px 0 0 -2px rgba(255, 237, 213, 0.95),
      178px 0 0 -1px rgba(245, 158, 11, 0.8),
      226px 0 0  0   rgba(253, 230, 138, 0.7);
    filter: blur(0.4px);
  }
  .product-embers::before {
    left: 7%;
    animation: divineEmber 2.9s ease-out infinite;
  }
  .product-embers::after {
    left: 31%;
    animation: divineEmber 3.6s ease-out infinite 0.8s;
  }
  @keyframes divineEmber {
    0%   { transform: translateY(0) scale(0.6);   opacity: 0; }
    12%  {                                        opacity: 1; }
    75%  {                                        opacity: 0.85; }
    100% { transform: translateY(-330px) scale(1.15); opacity: 0; }
  }
  /* Divine gold thread along the top of each card — the light travels it */
  .product-card .product-img-wrap::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 3px;
    z-index: 4;
    background: linear-gradient(90deg, transparent, #fde68a 20%, #f59e0b 50%, #fde68a 80%, transparent);
    background-size: 200% 100%;
    animation: divineThread 5s linear infinite;
  }
  @keyframes divineThread {
    from { background-position: 200% 0; }
    to   { background-position: -200% 0; }
  }

  /* Warm aura breathing over the product */
  .product-aura {
    position: absolute;
    inset: 0;
    z-index: 2;
    pointer-events: none;
    background: radial-gradient(58% 54% at 50% 44%, rgba(251, 191, 36, 0.32), transparent 72%);
    mix-blend-mode: screen;
    animation: divineAura 5.5s ease-in-out infinite;
  }
  @keyframes divineAura {
    0%, 100% { opacity: 0.3; }
    50%      { opacity: 0.85; }
  }

  /* Sparks that twinkle in place, then rest */
  .product-spark {
    position: absolute;
    z-index: 3;
    color: #fffbeb;
    font-size: 0.95rem;
    line-height: 1;
    opacity: 0;
    pointer-events: none;
    text-shadow: 0 0 12px rgba(251, 191, 36, 0.95);
    animation: divineTwinkle 3.4s ease-in-out infinite;
  }
  .product-spark-1 { top: 14%; right: 15%; }
  .product-spark-2 { bottom: 20%; left: 12%; font-size: 0.75rem; }
  @keyframes divineTwinkle {
    0%, 68%, 100% { opacity: 0;    transform: scale(0.5) rotate(0deg); }
    14%           { opacity: 1;    transform: scale(1.2) rotate(90deg); }
    30%           { opacity: 0.3;  transform: scale(0.8) rotate(150deg); }
    46%           { opacity: 0.95; transform: scale(1)   rotate(210deg); }
  }

  /* The lamp on the badge keeps flickering */
  .product-badge-flame {
    display: inline-block;
    animation: divineFlicker 1.7s ease-in-out infinite;
  }
  @keyframes divineFlicker {
    0%, 100% { transform: scale(1)    rotate(-3deg); }
    25%      { transform: scale(1.16) rotate(4deg); }
    55%      { transform: scale(0.94) rotate(-4deg); }
    78%      { transform: scale(1.08) rotate(3deg); }
  }
  .product-card:hover {
    transform: translateY(-12px) scale(1.015);
    box-shadow:
      0 30px 60px rgba(194, 65, 12, 0.32),
      0 0 0 1px rgba(251, 191, 36, 0.35) !important;
  }
  /* Warm the card body text container so the gradient reads through */
  .product-card .card-body {
    background: transparent;
  }

  .product-img-wrap {
    position: relative;
    overflow: hidden;
  }
  /* the photo fades into the cream body instead of stopping at a hard edge */
  .product-img-wrap::after {
    content: '';
    position: absolute;
    left: 0; right: 0; bottom: 0;
    height: 34%;
    z-index: 2;
    pointer-events: none;
    background: linear-gradient(to top, rgba(255, 245, 230, 0.92), transparent);
  }
  .product-image {
    height: clamp(200px, 26vw, 280px);
    width: 100%;
    object-fit: cover;
    transition: transform 0.45s ease;
  }
  .product-card:hover .product-image {
    transform: scale(1.08);
  }

  /* Hover overlay with Quick View cue */
  .product-overlay {
    position: absolute;
    inset: 0;
    z-index: 4;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(to top, rgba(17,24,39,0.55), rgba(17,24,39,0));
    opacity: 0;
    transition: opacity 0.35s ease;
  }
  .product-card:hover .product-overlay {
    opacity: 1;
  }
  .product-overlay-btn {
    background: rgba(255,255,255,0.95);
    color: #111827;
    font-weight: 700;
    font-size: 0.9rem;
    padding: 10px 20px;
    border: none;
    border-radius: 50px;
    cursor: pointer;
    transform: translateY(12px);
    transition: transform 0.35s ease, background 0.25s ease;
    box-shadow: 0 8px 20px rgba(0,0,0,0.25);
  }
  .product-card:hover .product-overlay-btn {
    transform: translateY(0);
  }
  .product-overlay-btn:hover {
    background: #fbbf24;
  }

  .product-badge {
    position: absolute;
    top: 15px; left: 15px;
    z-index: 3;
    background: linear-gradient(135deg, #7c2d12, #ea580c);
    color: #fde68a;
    border: 1px solid rgba(253,230,138,0.6);
    padding: 7px 14px;
    border-radius: 30px;
    font-size: 12px;
    font-weight: bold;
    box-shadow: 0 4px 12px rgba(180,83,9,0.35);
  }
  .product-stock-badge {
    position: absolute;
    top: 15px; right: 15px;
    z-index: 3;
    color: #fff;
    padding: 7px 12px;
    border-radius: 30px;
    font-size: 12px;
    font-weight: bold;
    box-shadow: 0 4px 10px rgba(0,0,0,0.2);
  }
  .product-stock-badge.is-low {
    background: linear-gradient(135deg, #f59e0b, #ea580c);
    animation: stockPulse 1.8s ease-in-out infinite;
  }
  .product-stock-badge.is-out {
    background: rgba(15,23,42,0.85);
    backdrop-filter: blur(4px);
  }
  @keyframes stockPulse {
    0%, 100% { transform: scale(1); box-shadow: 0 4px 10px rgba(234,88,12,0.35); }
    50% { transform: scale(1.06); box-shadow: 0 6px 16px rgba(234,88,12,0.6); }
  }

  .product-name {
    font-size: 1.22rem;
    letter-spacing: -0.01em;
    color: #1c1917;
    transition: color 0.25s ease;
  }
  .product-card:hover .product-name { color: #9a3412; }

  .product-stars {
    font-size: 0.85rem;
    letter-spacing: 1px;
  }

  /*
    Price and delivery sit on their own raised plate rather than loose in the
    body — it gives the number somewhere to belong and separates the buying
    information from the description above it.
  */
  .product-price-plate {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    flex-wrap: wrap;
    padding: 11px 14px;
    border-radius: 16px;
    background:
      linear-gradient(135deg, rgba(255,255,255,0.95), rgba(255,247,232,0.85));
    border: 1px solid rgba(245, 158, 11, 0.3);
    box-shadow:
      inset 0 1px 0 rgba(255,255,255,0.9),
      0 4px 14px rgba(180, 83, 9, 0.1);
  }

  /* The figure is the loudest thing in the body, so it carries the gradient */
  .product-price {
    font-size: 1.7rem;
    font-weight: 800;
    line-height: 1;
    letter-spacing: -0.02em;
    background: linear-gradient(135deg, #b45309, #ea580c 45%, #f59e0b);
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
    -webkit-text-fill-color: transparent;
  }
  .product-price-cur {
    font-size: 1.1rem;
    font-weight: 700;
    margin-right: 1px;
    vertical-align: 0.12em;
  }

  .product-ship {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 5px 11px;
    border-radius: 999px;
    background: rgba(22, 163, 74, 0.12);
    border: 1px solid rgba(22, 163, 74, 0.28);
    color: #15803d;
    font-size: 0.75rem;
    font-weight: 700;
    white-space: nowrap;
  }
  /* the van keeps setting off, so the offer reads as active */
  .product-ship-ico {
    display: inline-block;
    animation: divineDeliver 3.6s ease-in-out infinite;
  }
  @keyframes divineDeliver {
    0%, 62%, 100% { transform: translateX(0); }
    72%           { transform: translateX(3px); }
    84%           { transform: translateX(-2px); }
  }

  /*
    The primary action is a gradient rather than Bootstrap's flat dark, with
    a light wiping across it on hover. Bootstrap sets its own background on
    .btn-dark at a higher specificity, hence the override.
  */
  .product-btn {
    position: relative;
    overflow: hidden;
    border-radius: 14px;
    font-weight: 700;
    letter-spacing: 0.01em;
    padding: 11px;
    border: none;
    transition: transform 0.25s ease, box-shadow 0.25s ease;
  }
  .product-card .btn-dark.product-btn {
    background: linear-gradient(135deg, #1c1917, #292524 45%, #431407) !important;
    color: #fef3c7 !important;
    box-shadow: 0 8px 18px rgba(28, 25, 23, 0.28);
  }
  .product-card .btn-dark.product-btn:hover {
    background: linear-gradient(135deg, #431407, #7c2d12 45%, #ea580c) !important;
    color: #fff7ed !important;
  }
  .product-card .btn-warning.product-btn {
    background: linear-gradient(135deg, #fbbf24, #f59e0b) !important;
    color: #442c00 !important;
    box-shadow: 0 8px 18px rgba(245, 158, 11, 0.32);
  }
  .product-btn::after {
    content: '';
    position: absolute;
    top: 0;
    left: -60%;
    width: 40%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.35),
      transparent
    );
    transform: skewX(-18deg);
  }
  .product-btn:hover::after { animation: divineBtnWipe 0.7s ease-out; }
  @keyframes divineBtnWipe {
    from { left: -60%; }
    to   { left: 130%; }
  }
  .product-btn:hover {
    transform: translateY(-3px);
    box-shadow: 0 14px 26px rgba(124, 45, 18, 0.32);
  }
  .product-btn-arrow {
    display: inline-block;
    transition: transform 0.25s ease;
  }
  .product-btn:hover .product-btn-arrow {
    transform: translateX(5px);
  }

  /* Loading skeletons */
  .skeleton-box, .skeleton-line {
    background: linear-gradient(90deg, #e5e7eb 25%, #f3f4f6 37%, #e5e7eb 63%);
    background-size: 400% 100%;
    animation: skeletonShimmer 1.4s ease infinite;
  }
  .skeleton-line {
    height: 14px;
    border-radius: 6px;
  }
  @keyframes skeletonShimmer {
    0% { background-position: 100% 50%; }
    100% { background-position: 0 50%; }
  }

  /* Card entrance animation */
  .product-fade-in {
    animation: productFadeUp 0.55s ease both;
  }
  @keyframes productFadeUp {
    from { opacity: 0; transform: translateY(18px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @media (prefers-reduced-motion: reduce) {
    .product-fade-in, .skeleton-box, .skeleton-line,
    .product-stock-badge.is-low,
    .product-card .product-img-wrap::before,
    .product-aura, .product-badge-flame,
    .product-hero-glow, .product-hero-rays, .product-hero-shine,
    .product-hero-spark, .product-heading-flame,
    .product-card, .product-ship-ico,
    .product-embers::before, .product-embers::after,
    .product-btn:hover::after { animation: none; }
    .product-spark { display: none; }
    .product-aura { opacity: 0.35; }
    /* these are motion by definition, so with motion off they do not show */
    .product-embers { display: none; }
    .product-btn::after { display: none; }
    .product-card:hover { transform: none; }
  }

  @media (max-width: 575.98px) {
    .product-hero::after { inset: 6px; border-radius: 18px; }
    .product-hero-eyebrow { letter-spacing: 2px; font-size: 0.64rem; }
    .product-hero-chip { font-size: 0.72rem; padding: 6px 12px; }
  }
`;

export default ProductSection;
